import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift, Plus, AlertCircle, Download } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency, fmtDate } from "@/lib/format";

export default function Referrals() {
  const { selected, isAll, currentClient } = useClient();
  const { isReadOnly } = useAuth();
  const qc = useQueryClient();
  const single = !isAll && selected;

  const { data: referrals = [], isLoading } = useQuery({
    queryKey: ["referrals", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/referrals`)).data,
    enabled: !!single,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["referrals"] });

  const download = async () => {
    const res = await api.get(`/clients/${selected}/export`, { params: { dataset: "referrals" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "referrals.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (isAll) return <div><PageHeader title="Referrals" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to manage its referral program." /></div>;

  const inProgram = new Set(referrals.map((r) => r.referrer_contact_id)).size;
  const generated = referrals.filter((r) => r.referral_name).length;
  const converted = referrals.filter((r) => r.referral_converted);
  const revenue = converted.reduce((s, r) => s + (Number(r.referral_deal_value) || 0), 0);

  return (
    <div>
      <PageHeader title="Referrals" subtitle={currentClient?.business_name}>
        <Button variant="outline" onClick={download} data-testid="export-referrals-btn"><Download className="h-4 w-4 mr-1.5" /> Export</Button>
        {!isReadOnly && <AddReferralDialog clientId={selected} onDone={refresh} />}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <MiniStat label="Customers in Program" value={inProgram} />
        <MiniStat label="Referral Requests" value={referrals.filter((r) => r.request_date).length} />
        <MiniStat label="Referrals Generated" value={generated} />
        <MiniStat label="Referral Deals" value={converted.length} />
        <MiniStat label="Referral Revenue" value={fmtCurrency(revenue)} accent />
      </div>

      {isLoading ? <Spinner /> : referrals.length === 0 ? (
        <EmptyState icon={Gift} title="No referrals yet" subtitle="When a customer becomes a DEAL, enroll them in the referral program." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Customer</TableHead><TableHead>Referral Name</TableHead><TableHead>Contact Info</TableHead>
                  <TableHead>Status</TableHead><TableHead>Converted</TableHead><TableHead className="text-right">Deal Value</TableHead>
                  {!isReadOnly && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((r) => (
                  <TableRow key={r.id} data-testid={`referral-row-${r.id}`}>
                    <TableCell className="font-semibold text-slate-900">{r.referrer_name || r.customer_name}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.referral_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.referral_contact_info || "—"}</TableCell>
                    <TableCell><Badge variant="outline">{r.referral_status || "New"}</Badge></TableCell>
                    <TableCell>{r.referral_converted ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Yes</Badge> : <span className="text-slate-400 text-sm">No</span>}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-slate-700">{r.referral_deal_value ? fmtCurrency(r.referral_deal_value) : "—"}</TableCell>
                    {!isReadOnly && <TableCell><UpdateReferralDialog clientId={selected} referral={r} onDone={refresh} /></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {referrals.map((r) => (
              <div key={r.id} data-testid={`referral-card-${r.id}`} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.referrer_name || r.customer_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">&rarr; {r.referral_name || "—"}{r.referral_contact_info ? ` · ${r.referral_contact_info}` : ""}</div>
                  </div>
                  {r.referral_deal_value ? <span className="font-semibold tabular-nums text-slate-700 shrink-0">{fmtCurrency(r.referral_deal_value)}</span> : null}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline">{r.referral_status || "New"}</Badge>
                    {r.referral_converted ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Converted</Badge> : null}
                  </div>
                  {!isReadOnly && <UpdateReferralDialog clientId={selected} referral={r} onDone={refresh} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (<div className={`rounded-xl border p-4 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
    <div className="text-xl font-heading font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
  </div>);
}

function AddReferralDialog({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ referrer_contact_id: "", referral_name: "", referral_contact_info: "", request_date: "" });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const { data: eligible = [] } = useQuery({
    queryKey: ["eligible", clientId],
    queryFn: async () => (await api.get(`/clients/${clientId}/referrals/eligible`)).data,
    enabled: open,
  });

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/referrals`, { ...f, enrolled: true, create_lead: true, request_date: f.request_date || null })).data,
    onSuccess: () => { toast.success("Referral recorded"); setOpen(false); setF({ referrer_contact_id: "", referral_name: "", referral_contact_info: "", request_date: "" }); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-referral-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Referral</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Record Referral</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Referring Customer (DEAL)</Label>
            <Select value={f.referrer_contact_id} onValueChange={(v) => upd("referrer_contact_id", v)}>
              <SelectTrigger className="mt-1" data-testid="referrer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {eligible.length === 0 ? <div className="px-2 py-3 text-sm text-slate-400">No DEAL-stage customers yet</div> :
                  eligible.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Referral Name</Label><Input data-testid="referral-name" value={f.referral_name} onChange={(e) => upd("referral_name", e.target.value)} placeholder="Person being referred" className="mt-1" /></div>
          <div><Label>Referral Contact Info</Label><Input value={f.referral_contact_info} onChange={(e) => upd("referral_contact_info", e.target.value)} placeholder="Phone or email" className="mt-1" /></div>
          <p className="text-xs text-blue-600">The referred person is added as a new LEAD with source "Referral".</p>
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={!f.referrer_contact_id || mut.isPending} data-testid="save-referral-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateReferralDialog({ clientId, referral, onDone }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(referral.referral_status || "New");
  const [converted, setConverted] = useState(referral.referral_converted ? "yes" : "no");
  const [value, setValue] = useState(referral.referral_deal_value || "");
  const mut = useMutation({
    mutationFn: async () => (await api.put(`/clients/${clientId}/referrals/${referral.id}`, {
      referral_status: status, referral_converted: converted === "yes", referral_deal_value: value ? Number(value) : null,
    })).data,
    onSuccess: () => { toast.success("Referral updated"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`update-referral-${referral.id}`}>Update</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Update Referral</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["New", "Contacted", "Qualified", "Converted", "Lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Converted to Deal?</Label>
            <Select value={converted} onValueChange={setConverted}>
              <SelectTrigger className="mt-1" data-testid="referral-converted-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
            </Select>
          </div>
          {converted === "yes" && <div><Label>Referral Deal Value</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" data-testid="referral-deal-value" /></div>}
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-referral-update-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
